/**
 * WorldScene — the hero visual. A fixed aspect room asset with layered slots for
 * mascot + unlockable PNG/WebP objects, positioned by percentage so artwork
 * can be swapped without changing layout.
 */
import Mascot from "./Mascot";

// Positions are in % of the room container (left, top). Later real assets replace placeholders.
export const UNLOCK_SLOTS = {
  // drum unlocks (right half of room)
  drum_sticks:   { x: 72, y: 68, w: 10 },
  snare_upgrade: { x: 76, y: 60, w: 14 },
  hi_hat:        { x: 86, y: 46, w: 12 },
  new_cymbal:    { x: 64, y: 48, w: 14 },
  drum_skin:     { x: 76, y: 60, w: 16 },
  stage_lights:  { x: 76, y: 10, w: 36 },
  // english unlocks (left half)
  books:         { x: 32, y: 48, w: 8 },
  desk_lamp:     { x: 12, y: 42, w: 10 },
  plant:         { x: 36, y: 50, w: 10 },
  wall_poster:   { x: 20, y: 14, w: 20 },
  desk_upgrade:  { x: 18, y: 62, w: 24 },
  // perfect
  rug:           { x: 38, y: 76, w: 32 },
  trophy:        { x: 30, y: 46, w: 7 },
};

function UnlockPlaceholder({ code, size }) {
  // Small floating designer-toy-style token (not a flat icon), to be swapped with PNG asset later.
  const PALETTE = {
    drums:   { bg: "hsl(252 56% 66%)", dot: "hsl(42 80% 72%)" },
    english: { bg: "hsl(42 80% 72%)",  dot: "hsl(252 56% 66%)" },
    perfect: { bg: "hsl(346 70% 72%)", dot: "hsl(42 80% 72%)" },
  };
  const kind = code.includes("drum") || ["hi_hat","new_cymbal","snare_upgrade","drum_sticks","drum_skin","stage_lights"].includes(code)
    ? "drums"
    : ["rug","trophy"].includes(code) ? "perfect" : "english";
  const p = PALETTE[kind];
  return (
    <div
      aria-hidden
      style={{ width: size, height: size }}
      className="rounded-full flex items-center justify-center shadow-[0_12px_24px_-8px_rgba(0,0,0,0.25)]"
    >
      <div className="w-full h-full rounded-full relative" style={{ background: p.bg }}>
        <div className="absolute left-1/2 -translate-x-1/2 top-1 w-1/3 h-1/4 rounded-full bg-white/40 blur-[1px]" />
        <div className="absolute left-1/2 -translate-x-1/2 bottom-[18%] w-1/2 h-1/2 rounded-full" style={{ background: p.dot }} />
      </div>
    </div>
  );
}

export default function WorldScene({ ownedCodes = [], mascotState = "idle", className = "", testId = "world-scene", mascotSize = 180, roomImage = "/assets/world/bedroom.png" }) {
  return (
    <div
      data-testid={testId}
      className={`relative w-full ${className}`}
      style={{ aspectRatio: "4 / 3" }}
    >
      {/* soft stage glow behind the room */}
      <div className="absolute inset-0 rounded-[36px]"
        style={{
          background: "radial-gradient(60% 70% at 50% 70%, hsla(252,56%,66%,0.14), transparent 70%), radial-gradient(50% 60% at 50% 10%, hsla(42,80%,72%,0.18), transparent 60%)",
        }}
      />

      {/* Base room asset */}
      <img
        src={roomImage}
        alt=""
        draggable={false}
        className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
      />

      {/* Unlock layers */}
      {ownedCodes.map((code) => {
        const slot = UNLOCK_SLOTS[code];
        if (!slot) return null;
        // We don't have real PNGs yet — show a soft 3D token placeholder sized to slot
        return (
          <div
            key={code}
            className="absolute animate-floaty"
            style={{
              left: `${slot.x}%`,
              top: `${slot.y}%`,
              width: `${slot.w}%`,
              transform: "translate(-50%, -50%)",
              aspectRatio: "1 / 1",
            }}
            data-unlock={code}
          >
            {/* Future: <img src={`/assets/world/unlocks/${code}.png`} .../> */}
            <UnlockPlaceholder code={code} size="100%" />
          </div>
        );
      })}

      {/* Mascot */}
      <div
        className="absolute left-1/2 -translate-x-1/2"
        style={{ bottom: "6%" }}
      >
        <Mascot state={mascotState} size={mascotSize} />
      </div>
    </div>
  );
}
